import { format } from 'date-fns';
import { el } from 'date-fns/locale';

// The generated DTOs type some date fields as the `String` wrapper object rather than the
// `string` primitive; accept either (anything stringifiable) so call sites need no coercion.
type DateLike = string | { toString(): string } | null | undefined;

/** Format an ISO date string as `dd MMM yyyy HH:mm` (el-GR). Returns '—' for null/invalid input. */
export function formatDateTime(iso?: DateLike): string {
	if (!iso) return '—';
	const d = new Date(String(iso));
	return Number.isNaN(d.getTime()) ? '—' : format(d, 'dd MMM yyyy HH:mm', { locale: el });
}

/** Format an ISO date string as `dd MMM yyyy` (el-GR). Returns '—' for null/invalid input. */
export function formatDate(iso?: DateLike): string {
	if (!iso) return '—';
	const d = new Date(String(iso));
	return Number.isNaN(d.getTime()) ? '—' : format(d, 'dd MMM yyyy', { locale: el });
}

/**
 * Normalise a server date to a UTC ISO string. The API returns naive-UTC strings (no zone), so a
 * bare `new Date(s)` would parse them as LOCAL time; append `Z` when no zone is present. Returns
 * null for null/invalid input. Used by the banner/news schema mappers (legacy parity).
 */
export function toIsoUtc(raw?: DateLike): string | null {
	if (!raw) return null;
	const s = String(raw);
	const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
	return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * A calendar day as `yyyy-MM-dd`, read in the VIEWER's zone.
 *
 * Deliberately not `toISOString().slice(0, 10)`: that converts to UTC first, so an administrator east of
 * Greenwich picking a date late in the evening would file it under the previous day. Used for date-only
 * fields — a settlement date, a dispatch date, a filter bound — where the day is what is meant and the
 * time of day is not.
 */
export function toIsoDay(date: Date): string {
	const pad = (n: number) => String(n).padStart(2, '0');

	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Largest-first, so the first unit the elapsed time fills is the one used. */
const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
	['day', 86400],
	['hour', 3600],
	['minute', 60],
	['second', 1]
];

/**
 * Elapsed time as a localized relative phrase — "2 hours ago", "πριν από 2 ώρες" — via the platform's
 * Intl.RelativeTimeFormat, so plural rules come from the runtime rather than from locale keys. Returns
 * '—' for null/invalid input.
 *
 * Pass an already-normalised instant: the API returns naive-UTC strings, so callers should hand this
 * `toIsoUtc(raw)` rather than the raw field, or every label is out by the viewer's offset.
 */
export function formatRelativeTime(iso: DateLike, language: string): string {
	if (!iso) return '—';
	const date = new Date(String(iso));
	if (Number.isNaN(date.getTime())) return '—';

	const elapsedSeconds = (date.getTime() - Date.now()) / 1000;
	const [unit, size] = RELATIVE_UNITS.find(([, seconds]) => Math.abs(elapsedSeconds) >= seconds) ?? RELATIVE_UNITS[RELATIVE_UNITS.length - 1];

	return new Intl.RelativeTimeFormat(language || 'el', { numeric: 'auto' }).format(Math.round(elapsedSeconds / size), unit);
}
